/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR557: S1.4 Attempt Timeline
 * CR606: Enhanced with Pass B trace, duration, and backfill badges.
 * CR612: Added Cancel button for RUNNING attempts.
 * Shows history of all S1.4 attempts with status, counts, pass_b decision,
 * failure reason codes, and prompt pack info for instant debugging.
 * "I never want to open SQL for this again."
 */
import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Clock,
  Gem,
  Bot,
  ShieldAlert,
  Zap,
  StopCircle,
  Ban,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

interface AttemptRow {
  id: string;
  attempt_no: number;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  diagnostics_json: Record<string, unknown> | null;
  prompt_pack_snapshot_id: string | null;
  error_message: string | null;
  config_json: Record<string, unknown> | null;
  // CR612
  cancel_requested_at: string | null;
  cancel_requested_by: string | null;
  cancel_reason: string | null;
  cancelled_at: string | null;
}

interface PassBSummary {
  aiInvoked: boolean;
  aiInvokedCount: number;
  total: number;
  usedCount: number;
  used: boolean;
  failReason: string | null;
  parseMode: string | null;
  coordSpace: string | null;
  rawPreview: string | null;
  aiSkipReason: string | null;
  decisionHistogram: Record<string, number> | null;
  failReasonHistogram: Record<string, number> | null;
}

interface AttemptSummary {
  id: string;
  attemptNo: number;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  located: number;
  cropped: number;
  filtered: number;
  errors: number;
  passBDecision: string | null;
  passB: PassBSummary | null;
  failCode: string | null;
  failStage: string | null;
  failDetail: string | null;
  locatorPackId: string | null;
  cropperPackId: string | null;
  runPolicy: string | null;
  errorMessage: string | null;
  copiedForward: number | null;
  isDiamond: boolean;
  closedByBackfill: boolean;
  closedBySafetyNet: boolean;
  candidateCount: number;
  artifactsInserted: number;
  artifactsAttempted: number;
  // CR607: No-work short-circuit info
  noWork: { reason_code: string; detail: string } | null;
  // CR612: Cancellation info
  cancelRequestedAt: string | null;
  cancelledBy: string | null;
  cancelReason: string | null;
  cancelledAt: string | null;
}

function extractPassB(d: Record<string, unknown>): PassBSummary | null {
  const pb = d.pass_b as Record<string, unknown> | undefined;
  if (!pb || typeof pb !== "object") return null;
  return {
    aiInvoked: (pb.ai_invoked as boolean) ?? false,
    aiInvokedCount: (pb.ai_invoked_count as number) ?? 0,
    total: (pb.total as number) ?? 0,
    usedCount: (pb.used_count as number) ?? 0,
    used: (pb.used as boolean) ?? false,
    failReason: (pb.fail_reason as string) ?? null,
    parseMode: (pb.parse_mode as string) ?? null,
    coordSpace: (pb.coord_space_detected as string) ?? null,
    rawPreview: (pb.raw_text_preview as string) ?? null,
    aiSkipReason: (pb.ai_skip_reason as string) ?? null,
    decisionHistogram: (pb.decision_histogram as Record<string, number>) ?? null,
    failReasonHistogram: (pb.fail_reason_histogram as Record<string, number>) ?? null,
  };
}

function extractSummary(row: AttemptRow): AttemptSummary {
  const d = row.diagnostics_json ?? {};
  const cfg = row.config_json ?? {};

  const passB = extractPassB(d);
  // Legacy fallback for pass_b decision if CR604 data not yet present
  const legacyPassBDecision = (d.pass_b_decision as string) ?? null;

  return {
    id: row.id,
    attemptNo: row.attempt_no,
    status: row.status,
    startedAt: row.started_at,
    finishedAt: row.finished_at,
    durationMs: row.duration_ms ?? (d.duration_ms as number) ?? null,
    located: (d.artifacts_inserted as number) ?? (d.located as number) ?? 0,
    cropped: (d.cropper_used_count as number) ?? 0,
    filtered: (d.filtered_out_by_attempt as number) ?? 0,
    errors: ((d.artifact_insert_errors as unknown[])?.length) ?? (d.errors as number) ?? 0,
    passBDecision: passB?.decisionHistogram
      ? Object.entries(passB.decisionHistogram).map(([k, v]) => `${k}:${v}`).join(", ")
      : legacyPassBDecision,
    passB,
    failCode: (d.failure_code as string) ?? (d.fail_code as string) ?? null,
    failStage: (d.failure_stage as string) ?? null,
    failDetail: (d.failure_detail as string) ?? null,
    locatorPackId: row.prompt_pack_snapshot_id ?? (cfg.prompt_pack_id as string) ?? null,
    cropperPackId: (cfg.cropper_prompt_pack_id as string) ?? null,
    runPolicy: (d.effective_run_policy as string) ?? null,
    errorMessage: row.error_message,
    copiedForward: (d.copied_forward_count as number) ?? null,
    isDiamond: (d.artifact_write_mode as string) === "insert" || (d.diamond_guard_active as boolean) === true,
    closedByBackfill: (d.cr603_backfill_closed as boolean) === true || (d.closed_by as string) === "backfill",
    closedBySafetyNet: (d.cr602_safety_net as boolean) === true || (d.cr551_zombie_prevention as boolean) === true,
    candidateCount: (d.candidate_count as number) ?? 0,
    artifactsInserted: (d.artifacts_inserted as number) ?? 0,
    artifactsAttempted: (d.artifacts_attempted as number) ?? 0,
    // CR607
    noWork: d.no_work ? { reason_code: (d.no_work as any).reason_code, detail: (d.no_work as any).detail } : null,
    // CR612
    cancelRequestedAt: row.cancel_requested_at ?? null,
    cancelledBy: row.cancel_requested_by ?? (d.cr612_cancel_requested_by as string) ?? null,
    cancelReason: row.cancel_reason ?? (d.cr612_cancel_reason as string) ?? null,
    cancelledAt: row.cancelled_at ?? null,
  };
}

function statusIcon(status: string) {
  switch (status) {
    case "COMPLETE":
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    case "COMPLETE_WITH_ERRORS":
      return <CheckCircle2 className="h-3.5 w-3.5 text-amber-500" />;
    case "FAILED":
      return <XCircle className="h-3.5 w-3.5 text-destructive" />;
    case "RUNNING":
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />;
    case "CANCELLED":
      return <Ban className="h-3.5 w-3.5 text-amber-500" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

function statusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "COMPLETE": return "default";
    case "COMPLETE_WITH_ERRORS": return "secondary";
    case "FAILED": return "destructive";
    case "CANCELLED": return "secondary";
    default: return "outline";
  }
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/** Pass B trace row — shown when expanded */
function PassBTrace({ pb }: { pb: PassBSummary }) {
  return (
    <div className="mt-1 pl-6 space-y-0.5 text-[10px]">
      {/* AI invocation status */}
      <div className="flex items-center gap-1.5">
        <Bot className="h-3 w-3 text-muted-foreground" />
        <span className={pb.aiInvoked ? "text-foreground" : "text-muted-foreground"}>
          AI: {pb.aiInvoked ? `invoked (${pb.aiInvokedCount}/${pb.total})` : "not invoked"}
        </span>
        {pb.aiSkipReason && !pb.aiInvoked && (
          <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-600">
            skip: {pb.aiSkipReason}
          </Badge>
        )}
      </div>

      {/* Decision histogram */}
      {pb.decisionHistogram && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-muted-foreground">Decisions:</span>
          {Object.entries(pb.decisionHistogram).map(([decision, count]) => (
            <Badge
              key={decision}
              variant="outline"
              className={cn("text-[8px] px-1 py-0", {
                "text-green-600 border-green-500/30": decision === "refined",
                "text-amber-600 border-amber-500/30": decision === "fallback_to_pass_a",
                "text-destructive border-destructive/30": decision === "internal_error",
                "text-muted-foreground": decision === "skipped",
              })}
            >
              {decision}: {count}
            </Badge>
          ))}
        </div>
      )}

      {/* Result summary */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {pb.usedCount > 0 && (
          <span className="text-green-600">✓ {pb.usedCount}/{pb.total} used</span>
        )}
        {pb.parseMode && (
          <Badge variant="outline" className="text-[8px] px-1 py-0">
            parse: {pb.parseMode}
          </Badge>
        )}
        {pb.coordSpace && pb.coordSpace !== "unknown" && (
          <Badge variant="outline" className="text-[8px] px-1 py-0">
            coords: {pb.coordSpace}
          </Badge>
        )}
      </div>

      {/* Fail reasons */}
      {pb.failReasonHistogram && Object.keys(pb.failReasonHistogram).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-destructive/70">Fail reasons:</span>
          {Object.entries(pb.failReasonHistogram).map(([reason, count]) => (
            <Badge key={reason} variant="outline" className="text-[8px] px-1 py-0 text-destructive/80 border-destructive/20">
              {reason.length > 30 ? reason.slice(0, 30) + "…" : reason}: {count}
            </Badge>
          ))}
        </div>
      )}

      {/* Raw preview */}
      {pb.rawPreview && (
        <div className="text-muted-foreground truncate max-w-[400px]" title={pb.rawPreview}>
          Raw: {pb.rawPreview.slice(0, 120)}{pb.rawPreview.length > 120 ? "…" : ""}
        </div>
      )}
    </div>
  );
}

interface S14AttemptTimelineProps {
  batchRunId: string;
  /** Max attempts to show, default 15 */
  limit?: number;
}

export function S14AttemptTimeline({ batchRunId, limit = 15 }: S14AttemptTimelineProps) {
  const [attempts, setAttempts] = useState<AttemptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [expandedAttempts, setExpandedAttempts] = useState<Set<string>>(new Set());
  const [cancelTarget, setCancelTarget] = useState<AttemptSummary | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [forceMode, setForceMode] = useState(false);

  const toggleAttemptExpand = (id: string) => {
    setExpandedAttempts(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    const { data } = await supabase
      .from("cr075_batch_run_step_attempts")
      .select("id, attempt_no, status, started_at, finished_at, duration_ms, diagnostics_json, prompt_pack_snapshot_id, error_message, config_json, cancel_requested_at, cancel_requested_by, cancel_reason, cancelled_at")
      .eq("batch_run_id", batchRunId)
      .in("step_key", ["S1_4", "S1_SNIPPET"])
      .not("status", "eq", "SUPERSEDED")
      .order("attempt_no", { ascending: false })
      .limit(limit);

    if (data) {
      setAttempts(data.map((r) => extractSummary(r as AttemptRow)));
    }
    setLoading(false);
  }, [batchRunId, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // CR553: Auto-poll every 5s when any attempt is RUNNING to detect cancel acknowledgement
  const hasRunning = attempts.some(a => a.status === "RUNNING");
  useEffect(() => {
    if (!hasRunning) return;
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [hasRunning, fetchData]);

  // CR553: Two-phase cancel — soft cancel first, then force cancel
  const handleCancelAttempt = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      if (forceMode) {
        // CR553: Force cancel — directly finalize the attempt in the DB
        const { data, error } = await supabase.rpc("force_cancel_step_attempt", {
          p_attempt_id: cancelTarget.id,
          p_reason: "Force cancelled by admin (CR553)",
        });
        if (error) {
          toast.error(`Force cancel failed: ${error.message}`);
        } else if (data && typeof data === "object" && !(data as any).ok) {
          toast.error(`Force cancel failed: ${(data as any).error}`);
        } else {
          toast.success("Attempt force-cancelled — status updated to CANCELLED");
          setTimeout(fetchData, 1000);
        }
      } else {
        // Soft cancel — request cancellation flag
        const { data, error } = await supabase.rpc("cancel_step_attempt", {
          p_attempt_id: cancelTarget.id,
          p_reason: "Admin cancelled via UI",
        });
        if (error) {
          toast.error(`Cancel failed: ${error.message}`);
        } else if (data && typeof data === "object" && !(data as any).ok) {
          toast.error(`Cancel failed: ${(data as any).error}`);
        } else {
          toast.success("Cancel requested — attempt will stop at next unit boundary");
          setTimeout(fetchData, 2000);
        }
      }
    } catch (err) {
      toast.error(`Cancel error: ${String(err)}`);
    } finally {
      setCancelling(false);
      setCancelTarget(null);
      setForceMode(false);
    }
  }, [cancelTarget, fetchData, forceMode]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading attempts…
      </div>
    );
  }

  if (attempts.length === 0) return null;

  const visible = expanded ? attempts : attempts.slice(0, 3);

  return (
    <TooltipProvider>
      <div className="space-y-1">
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => setExpanded((p) => !p)}
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          Attempt History ({attempts.length})
        </button>

        <div className="space-y-1">
          {visible.map((a) => {
            const isAttemptExpanded = expandedAttempts.has(a.id);
            const hasPassBData = a.passB != null && a.passB.total > 0;

            return (
              <div
                key={a.id}
                className={cn(
                  "px-2 py-1.5 rounded text-xs border transition-colors",
                  a.status === "FAILED" ? "border-destructive/30 bg-destructive/5" :
                  a.status === "COMPLETE" ? "border-green-500/30 bg-green-500/5" :
                  a.status === "RUNNING" ? "border-blue-500/30 bg-blue-500/5" :
                  a.status === "CANCELLED" ? "border-amber-500/30 bg-amber-500/5" :
                  "border-border bg-muted/30"
                )}
              >
                <div className="flex items-start gap-2">
                  {/* Left: status + attempt number */}
                  <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                    {statusIcon(a.status)}
                    <span className="font-mono font-medium">#{a.attemptNo}</span>
                    {a.isDiamond && (
                      <Tooltip>
                        <TooltipTrigger><Gem className="h-3 w-3 text-blue-400" /></TooltipTrigger>
                        <TooltipContent className="text-xs">Diamond Mode (attempt-scoped inserts)</TooltipContent>
                      </Tooltip>
                    )}
                    {a.closedByBackfill && (
                      <Tooltip>
                        <TooltipTrigger><ShieldAlert className="h-3 w-3 text-amber-500" /></TooltipTrigger>
                        <TooltipContent className="text-xs">Closed by backfill (CR603)</TooltipContent>
                      </Tooltip>
                    )}
                    {a.closedBySafetyNet && (
                      <Tooltip>
                        <TooltipTrigger><Zap className="h-3 w-3 text-orange-500" /></TooltipTrigger>
                        <TooltipContent className="text-xs">Closed by safety net (CR602 — was stuck RUNNING)</TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {/* Middle: key metrics */}
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={statusBadgeVariant(a.status)} className="text-[9px] px-1.5 py-0">
                        {a.status.replace(/_/g, " ")}
                      </Badge>
                      {a.located > 0 && (
                        <span className="text-green-600 dark:text-green-400">✓{a.located} located</span>
                      )}
                      {a.cropped > 0 && (
                        <span className="text-muted-foreground">🔲{a.cropped} cropped</span>
                      )}
                      {a.errors > 0 && (
                        <span className="text-destructive">✗{a.errors} errors</span>
                      )}
                      {a.copiedForward != null && a.copiedForward > 0 && (
                        <span className="text-muted-foreground">📋{a.copiedForward} copied</span>
                      )}
                      {a.candidateCount > 0 && (
                        <span className="text-muted-foreground">
                          {a.artifactsInserted}/{a.candidateCount} artifacts
                        </span>
                      )}
                      {a.noWork && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-muted-foreground">
                          {a.noWork.reason_code.replace(/_/g, " ")}
                        </Badge>
                      )}
                    </div>

                    {/* Failure info */}
                    {(a.failCode || a.errorMessage) && (
                      <div className="text-destructive/80 truncate" title={a.failDetail ?? a.errorMessage ?? undefined}>
                        {a.failStage && <span className="font-medium">[{a.failStage}] </span>}
                        {a.failCode ?? a.errorMessage}
                      </div>
                    )}

                    {/* Pass B summary line */}
                    <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                      {a.passB ? (
                        <button
                          type="button"
                          onClick={() => toggleAttemptExpand(a.id)}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          {isAttemptExpanded
                            ? <ChevronUp className="h-2.5 w-2.5" />
                            : <ChevronDown className="h-2.5 w-2.5" />
                          }
                          <Badge
                            variant="outline"
                            className={cn("text-[9px] px-1 py-0", {
                              "text-green-600 border-green-500/30": a.passB.used,
                              "text-amber-600 border-amber-500/30": !a.passB.used && a.passB.aiInvoked,
                              "text-muted-foreground": !a.passB.aiInvoked,
                            })}
                          >
                            {/* CR610: "skipped" not "fallback" when AI wasn't called */}
                            Pass B: {a.passB.used ? `${a.passB.usedCount} refined` : a.passB.aiInvoked ? "fallback" : "skipped"}
                            {a.passB.aiInvoked && <Bot className="h-2.5 w-2.5 ml-0.5 inline" />}
                          </Badge>
                          {a.passB.failReason && !a.passB.used && a.passB.aiInvoked && (
                            <span className="text-destructive/60 text-[9px] truncate max-w-[150px]" title={a.passB.failReason}>
                              {a.passB.failReason}
                            </span>
                          )}
                        </button>
                      ) : a.passBDecision ? (
                        <Badge variant="outline" className="text-[9px] px-1 py-0">
                          Pass B: {a.passBDecision}
                        </Badge>
                      ) : null}
                      {a.runPolicy && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0">
                          {a.runPolicy}
                        </Badge>
                      )}
                      {a.locatorPackId && (
                        <span className="truncate max-w-[100px]" title={a.locatorPackId}>
                          L:{a.locatorPackId.slice(0, 6)}
                        </span>
                      )}
                      {a.cropperPackId && (
                        <span className="truncate max-w-[100px]" title={a.cropperPackId}>
                          C:{a.cropperPackId.slice(0, 6)}
                        </span>
                      )}
                    </div>

                    {/* Expanded Pass B trace */}
                    {isAttemptExpanded && hasPassBData && <PassBTrace pb={a.passB!} />}
                  </div>

                  {/* Right: timing + cancel */}
                  <div className="text-muted-foreground text-right shrink-0 text-[10px] space-y-0.5">
                    <div>{formatDate(a.startedAt)} {formatTime(a.startedAt)}</div>
                    <div className="font-mono">{formatDuration(a.durationMs)}</div>
                    {a.finishedAt && a.startedAt && !a.durationMs && (
                      <div className="font-mono text-[9px]">
                        ~{formatDuration(new Date(a.finishedAt).getTime() - new Date(a.startedAt).getTime())}
                      </div>
                    )}
                    {/* CR553: Two-phase stop button for RUNNING attempts */}
                    {a.status === "RUNNING" && !a.cancelRequestedAt && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-5 px-1.5 text-[9px] mt-1"
                        onClick={(e) => { e.stopPropagation(); setCancelTarget(a); setForceMode(false); }}
                      >
                        <StopCircle className="h-2.5 w-2.5 mr-0.5" />
                        Stop
                      </Button>
                    )}
                    {/* CR553: Cancel was requested but attempt is still RUNNING — show Force Stop */}
                    {a.status === "RUNNING" && a.cancelRequestedAt && (
                      <div className="space-y-0.5">
                        <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-600 border-amber-400">
                          <Loader2 className="h-2 w-2 animate-spin mr-0.5" />
                          Stopping…
                        </Badge>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="h-5 px-1.5 text-[9px]"
                          onClick={(e) => { e.stopPropagation(); setCancelTarget(a); setForceMode(true); }}
                        >
                          <Ban className="h-2.5 w-2.5 mr-0.5" />
                          Force Stop
                        </Button>
                      </div>
                    )}
                    {/* CR612: Show who cancelled */}
                    {a.status === "CANCELLED" && a.cancelledBy && (
                      <div className="text-amber-600 text-[9px]">
                        by {a.cancelledBy.slice(0, 8)}…
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {!expanded && attempts.length > 3 && (
            <button
              type="button"
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors pl-2"
              onClick={() => setExpanded(true)}
            >
              +{attempts.length - 3} more attempts…
            </button>
          )}
        </div>
      </div>

      {/* CR553: Cancel/Force-cancel confirmation dialog */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) { setCancelTarget(null); setForceMode(false); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {forceMode ? "Force Stop" : "Stop"} S1.5 Attempt #{cancelTarget?.attemptNo}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {forceMode ? (
                <>
                  The soft cancel was requested but the attempt is still RUNNING.
                  This will <strong>immediately</strong> mark the attempt as CANCELLED in the database,
                  even if the edge function is no longer responding.
                  Work completed so far will be kept.
                </>
              ) : (
                <>
                  This will request cancellation. The attempt will stop at the next unit boundary.
                  If it doesn't stop within ~30 seconds, a "Force Stop" button will appear.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Running</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAttempt}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : forceMode ? <Ban className="h-3 w-3 mr-1" /> : <StopCircle className="h-3 w-3 mr-1" />}
              {forceMode ? "Force Stop Now" : "Stop Attempt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
